import debounce from 'lodash/debounce'
import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { updateSearchTerm } from '@/redux/ducks/lists'
import { useUpdateSearchParams } from '@/modules/_helpers/updateSearchParams'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row-reverse',
    border: theme.border,
    borderRadius: 4,
    background: '#fff',
    '&:not($disabled):hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      '& $icon': {
        color: theme.palette.primary.main,
      },
    },
    '&$disabled': {
      borderColor: theme.palette.text.disabled,
      '& $icon': {
        color: theme.palette.text.disabled,
      },
      '& $input': {
        color: theme.palette.text.disabled,
        backgroundColor: theme.white,
        '&::placeholder': {
          color: theme.palette.text.disabled,
        },
      },
    },
  },
  disabled: {},
  inputWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    borderRadius: 4,
    background: '#fff',
    width: '100%',
    padding: '0px 10px',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flex: '1 1 10%',
    paddingLeft: 20,
    color: theme.palette.text.secondary,
  },
  deleteIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    flex: '1 1 10%',
    paddingRight: 20,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  input: {
    border: 'none',
    outline: 'none',
    padding: '0px 10px',
    flex: '1 0 80%',
    color: theme.palette.text.primary,
    '&::-webkit-search-cancel-button, &::-webkit-search-decoration': {
      '-webkit-appearance': 'none',
      appearance: 'none',
    },
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  hidden: {
    visibility: 'hidden',
  },
}))

const Search = (props, forwardedRef) => {
  const dispatch = useDispatch()
  const {
    inputChanged,
    initValue,
    initValueOld,
    customClass = '',
    className = '',
    listSettingName,
    syncToUrl = true,
    disabled = false,
    ...rest
  } = props
  const classes = useStyles()
  const searchInput = useRef()

  // Pull the persisted value (if using Redux to store searchTerm)
  const listState = useSelector((state) => state.lists)
  const initValueFinal = listSettingName
    ? listState[listSettingName]?.searchTerm || ''
    : initValue

  // Local state for the input field
  const [inputValue, setInputValue] = useState(initValueFinal)
  const [hasValue, setHasValue] = useState(!!initValueFinal)

  // Expose focus method to parent refs
  useImperativeHandle(
    forwardedRef,
    () => ({
      onFocus,
    }),
    [],
  )

  const [searchParams, updateSearchParams] = useUpdateSearchParams()

  // On component mount, if there's a searchTerm in the URL, update the store
  useEffect(() => {
    if (!syncToUrl || !listSettingName) return
    if (searchParams.has('searchTerm')) {
      dispatch(updateSearchTerm(listSettingName, searchParams.get('searchTerm')))
    }
  }, [searchParams, syncToUrl, listSettingName, dispatch])

  // Watch for external changes to initValueFinal and update our input field
  useEffect(() => {
    if (searchInput.current && initValueFinal !== initValueOld) {
      searchInput.current.value = initValueFinal
      setInputValue(initValueFinal)
      setHasValue(!!initValueFinal)
      if (syncToUrl) {
        updateSearchParams([{ key: 'searchTerm', value: initValueFinal }])
      }
    }
  }, [searchInput, initValueFinal, initValueOld, syncToUrl, updateSearchParams])

  /**
   * 1) Create a stable debounced function.
   *    We use `useRef` so that the same debounced instance
   *    persists across re-renders (and doesn't reset the timer).
   */
  const debouncedOnInputChanged = useRef(
    debounce((value) => {
      // Dispatch only if value actually changed
      // from the last known (local) state
      setInputValue((prevValue) => {
        if (value !== prevValue) {
          if (listSettingName) {
            if (syncToUrl) {
              updateSearchParams([{ key: 'searchTerm', value }])
            }
            dispatch(updateSearchTerm(listSettingName, value))
          } else {
            inputChanged(value)
          }
          return value
        }
        return prevValue
      })
    }, 600),
  ).current

  /**
   * 2) On unmount, cancel any pending debounced calls
   *    to avoid memory leaks or calling setState after unmount.
   */
  useEffect(() => {
    return () => {
      debouncedOnInputChanged.cancel()
    }
  }, [debouncedOnInputChanged])

  // Clear input field
  const onDeleteClick = () => {
    if (!searchInput.current) return
    // Cancel any pending debounced input so a stale value can't reappear
    // after the field has been cleared.
    debouncedOnInputChanged.cancel()
    searchInput.current.value = ''
    setInputValue('')
    setHasValue(false)

    if (listSettingName) {
      dispatch(updateSearchTerm(listSettingName, ''))
    } else {
      inputChanged('')
    }
  }

  // Focus on the input field
  const onFocus = () => {
    if (searchInput.current) {
      searchInput.current.focus()
    }
  }

  /**
   * 3) Light wrapper around our debounced function.
   *    - We read the current input's value
   *    - Forward it to the debounced function
   */
  const onInput = (event) => {
    const value = event.target.value
    // Toggle the delete icon immediately, independent of the debounce
    setHasValue(!!value)
    // Call the debounced function
    debouncedOnInputChanged(value)
  }

  // Conditionally render the delete icon
  const deleteIcon = (
    <span className={clsx(classes.deleteIcon, !hasValue && classes.hidden)} onClick={onDeleteClick}>
      <CloseRoundedIcon fontSize='medium' color='inherit' />
    </span>
  )

  return (
    <div className={clsx(classes.container, disabled && classes.disabled, customClass, className)}>
      <div className={classes.inputWrapper}>
        <span className={classes.icon}>
          <SearchRoundedIcon fontSize='medium' color='inherit' />
        </span>
        <input
          name="list-search-input"
          ref={searchInput}
          type="search"
          disabled={disabled}
          onFocus={onFocus}
          onKeyUp={onInput}
          className={classes.input}
          defaultValue={inputValue}
          {...rest}
        />
        {deleteIcon}
      </div>
    </div>
  )
}

Search.propTypes = {
  inputChanged: PropTypes.func,
  customClass: PropTypes.string,
  className: PropTypes.string,
  initValue: PropTypes.string,
  initValueOld: PropTypes.string,
  iconSize: PropTypes.string,
  listSettingName: PropTypes.string,
  syncToUrl: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
}

export default forwardRef(Search)
